import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BoardLockServiceInterface } from '@crowdctrl/shared';

/**
 * Service to manage board lock state in the boards app.
 * Lock state is stored in localStorage and persists across sessions.
 * When locked, navigation away from the board page is blocked.
 */
@Injectable({
  providedIn: 'root',
})
export class BoardLockService implements BoardLockServiceInterface {
  private readonly STORAGE_KEY = 'boards-app-board-locked';
  private readonly PIN_STORAGE_KEY = 'boards-app-board-lock-pin';
  private readonly LOCKED_BOARD_ROUTE_KEY = 'boards-app-locked-board-route';
  private isLockedSubject = new BehaviorSubject<boolean>(this.loadLockState());
  public isLocked$: Observable<boolean> = this.isLockedSubject.asObservable();

  /**
   * Get current lock state synchronously
   */
  isLocked(): boolean {
    return this.isLockedSubject.value;
  }

  /**
   * Set the PIN for locking/unlocking
   */
  setPin(pin: string): void {
    localStorage.setItem(this.PIN_STORAGE_KEY, pin);
  }

  /**
   * Get the stored PIN
   */
  getPin(): string | null {
    return localStorage.getItem(this.PIN_STORAGE_KEY);
  }

  /**
   * Verify PIN
   */
  verifyPin(pin: string): boolean {
    const storedPin = this.getPin();
    return storedPin !== null && storedPin === pin;
  }

  /**
   * Check if PIN is set
   */
  hasPin(): boolean {
    return this.getPin() !== null;
  }

  /**
   * Lock the board (prevent navigation)
   */
  lock(): void {
    localStorage.setItem(this.STORAGE_KEY, 'true');
    this.isLockedSubject.next(true);
  }

  /**
   * Store the locked board route
   */
  setLockedBoardRoute(workspaceId: string, sessionId: string, boardId: string): void {
    const route = JSON.stringify({ workspaceId, sessionId, boardId });
    localStorage.setItem(this.LOCKED_BOARD_ROUTE_KEY, route);
  }

  /**
   * Get the locked board route
   */
  getLockedBoardRoute(): {
    workspaceId: string;
    sessionId: string;
    boardId: string;
  } | null {
    const stored = localStorage.getItem(this.LOCKED_BOARD_ROUTE_KEY);
    if (!stored) {
      return null;
    }
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  /**
   * Clear the locked board route
   */
  clearLockedBoardRoute(): void {
    localStorage.removeItem(this.LOCKED_BOARD_ROUTE_KEY);
  }

  /**
   * Unlock the board (allow navigation)
   */
  unlock(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.clearLockedBoardRoute();
    this.isLockedSubject.next(false);
  }

  /**
   * Lock with PIN - always sets/updates the PIN (no verification of old PIN required)
   * The PIN should be confirmed/verified by the UI before calling this method.
   */
  lockWithPin(pin: string): boolean {
    // Always set the PIN and lock (no verification of old PIN)
    this.setPin(pin);
    this.lock();
    return true;
  }

  /**
   * Unlock with PIN verification
   */
  unlockWithPin(pin: string): boolean {
    if (this.verifyPin(pin)) {
      this.unlock();
      return true;
    }
    return false;
  }

  /**
   * Toggle lock state (required by interface, but should use PIN entry in UI)
   * This is kept for interface compliance but should not be called directly.
   * Use lockWithPin/unlockWithPin instead.
   */
  toggle(): void {
    if (this.isLocked()) {
      this.unlock();
    } else {
      this.lock();
    }
  }

  /**
   * Load lock state from localStorage
   */
  private loadLockState(): boolean {
    if (typeof window === 'undefined' || !localStorage) {
      return false;
    }
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored === 'true';
  }
}
